import React, { useEffect, useCallback, useId, useRef, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '../button/Button';

// Open-modal stack. Modals can stack (a shortcuts dialog over a form modal), and each
// instance listens on document — without an owner, every open modal handles the same
// Escape (one keypress closes the whole stack, destroying any typed input below) and
// two Tab traps fight over focus. Key ownership goes to the modal whose panel holds
// focus, falling back to the top of the stack: push order alone can invert when two
// nested modals mount in the same commit (child effects run before the parent's).
type ModalStackEntry = { id: symbol; panelRef: React.RefObject<HTMLDivElement | null> };
const openModalStack: ModalStackEntry[] = [];

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [contenteditable]:not([contenteditable="false"]), [tabindex]:not([tabindex="-1"])';

export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'wide' | 'full';
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  disableCloseButton?: boolean;
  /**
   * Accessible name for the icon-only close button. This package carries no
   * i18n, so a localized app passes its own translated string; the default is
   * only right for English. Never leave it unnamed — a bare icon announces as
   * "button".
   */
  closeLabel?: string;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  footer?: ReactNode;
  zIndex?: number;
  children: ReactNode;
}

// Size configurations
const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  wide: 'max-w-4xl',
  full: 'max-w-6xl'
};

export const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  disableCloseButton = false,
  closeLabel = 'Close',
  className = '',
  contentClassName = '',
  headerClassName = '',
  footerClassName = '',
  footer,
  zIndex = 50,
  children
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [stackId] = useState(() => Symbol('BaseModal'));

  // Handle escape key + keep Tab inside the dialog. The trap lives at the document
  // level on purpose: opening a modal often disables the trigger that held focus
  // (blurring it to <body>), and a panel-scoped listener would never see the next
  // Tab — the operator would be tabbing through the page behind the backdrop.
  const handleEscape = useCallback((e: KeyboardEvent) => {
    const active = document.activeElement;
    // Only the owning modal handles keys: the one whose panel holds focus, else the
    // top of the stack (focus may sit on <body> or in a portaled menu — see below).
    const owner =
      openModalStack.find((entry) => entry.panelRef.current?.contains(active)) ??
      openModalStack[openModalStack.length - 1];
    if (owner?.id !== stackId) return;
    if (e.key === 'Escape' && closeOnEscape) {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    // Focus can legitimately sit outside the panel: Select/MultiSelect/ComboBox render
    // their menus in portals on document.body. Leave Tab alone there — yanking focus
    // out of an open menu is worse than one uncontained keystroke. Recapture only when
    // focus was genuinely lost (e.g. opening the modal disabled the trigger → <body>).
    const focusLost = !active || active === document.body;
    if (!focusLost && !panel.contains(active)) return;
    const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      // The panel itself holds initial focus (tabIndex -1) and sits before every
      // control: treat it like "before the first control", so the very first
      // Shift+Tab wraps to the last control instead of escaping into the page
      // behind the backdrop.
      if (focusLost || active === panel || active === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (focusLost || active === last) {
      e.preventDefault();
      first.focus();
    }
  }, [closeOnEscape, onClose, stackId]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      onClose();
    }
  }, [closeOnBackdrop, onClose]);

  // Stack registration lives in its own effect, keyed on isOpen only: the listener
  // effect below re-runs whenever handleEscape's identity changes (e.g. a consumer
  // flipping closeOnEscape mid-flight), and a pop+re-push there would shuffle this
  // modal to the top of the stack past a dialog genuinely stacked above it.
  useEffect(() => {
    if (!isOpen) return;
    openModalStack.push({ id: stackId, panelRef });
    return () => {
      const i = openModalStack.findIndex((entry) => entry.id === stackId);
      if (i !== -1) openModalStack.splice(i, 1);
    };
  }, [isOpen, stackId]);

  // Setup event listeners and prevent background scrolling
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent background scrolling
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen, handleEscape]);

  // Capture the focus-restore target during render, on the closed→open transition —
  // BEFORE the mount commit. An effect is too late: the same commit that mounts the
  // modal often disables the trigger button that held focus (browsers blur a disabled
  // element to <body>), and a consumer's autoFocus child also commits before effects
  // run — either way an effect-time document.activeElement is the wrong element and
  // closing would dump keyboard focus at the top of the page.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  if (isOpen && !wasOpenRef.current) {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
  }
  wasOpenRef.current = isOpen;

  // Initial focus + restore. Focusing the panel (not its first control) announces
  // the dialog to screen readers without pre-arming a button Enter could fire.
  // Skip when focus already landed inside — a consumer's autoFocus field wins.
  // On close, return focus to whatever had it before the dialog opened; if that
  // element is gone or disabled the call is a harmless no-op.
  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      panel.focus();
    }
    return () => {
      // Restore only when focus was lost WITH this modal's DOM (removing the focused
      // element drops focus on <body> — by cleanup time the panel is already gone).
      // If focus sits on a real element, leave it: a modal closing UNDER a still-open
      // dialog must not yank focus out of it into the page behind its backdrop —
      // that dialog's trap deliberately ignores focus on real outside elements, so
      // the whole background page would become keyboard-reachable.
      const active = document.activeElement;
      if (!active || active === document.body) {
        previouslyFocusedRef.current?.focus?.();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex }}
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={`
        bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full ${sizeClasses[size]}
        transform transition-all duration-200 ease-out max-h-[90vh] flex flex-col outline-none
        ${className}
      `}>
        {/* Header */}
        {title && (
          <div className={`flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 ${headerClassName}`}>
            <div id={titleId} className="text-xl font-semibold text-foreground">
              {title}
            </div>
            {showCloseButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                disabled={disableCloseButton}
                className="h-8 w-8 p-0"
                // Fall back rather than trust the string: a default only fires
                // on undefined, and the value this prop is most likely to get
                // is a translation lookup, which can arrive empty.
                aria-label={closeLabel.trim() || 'Close'}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        {/* Content - Only this area is scrollable */}
        <div className={`flex-1 overflow-y-auto ${contentClassName}`}>
          {children}
        </div>

        {/* Footer - Fixed at bottom */}
        {footer && (
          <div className={`flex-shrink-0 px-6 py-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg ${footerClassName}`}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
